import _ from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import BadgeIcon from '@mui/icons-material/Badge'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Logout from '@mui/icons-material/Logout'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded'
import SettingsIcon from '@mui/icons-material/Settings'
import SyncIcon from '@mui/icons-material/Sync'

import logoImg from '@/assets/img/logo.png'
import colors from '@/assets/scss/_themes-vars.module.scss'

import { useGetTimeTrackingConsentQuery } from '@/core/api'
import AccountMenu, { AccountMenuHandle } from '@/components/ui/menu/AccountMenu'
import { OfflineModeBadge } from '@/shared/offline/ui/OfflineStatusIndicator'
import SyncDialog from '@/components/ui/dialogs/SyncDialog'
import { FeatureFlag, useIsFlagEnabled } from '@/hooks/useIsFlagEnabled'
import { usePathPattern } from '@/hooks/usePathPattern'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAppSettings } from '@/redux/reducers/app.reducer'
import { logout, selectAuthUser, selectAuthUserSubscription } from '@/redux/reducers/auth.reducer'
import { getDownloadSpeed, getUploadSpeed, selectNetworkSpeed } from '@/redux/reducers/statistic.reducer'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'

import { SidebarItem } from '@/utils/subscription/types'
import { isStaff } from '@/utils/type-guards'

import { SidebarMobile } from '../MobileSidebar'
import { AboutDialog } from './AboutDialog'
import { Notifications } from './Notifications'
import { TimeTrackingNotifications } from '@/features/time-tracking'
import { Language } from './Language'
import { Button } from '@mui/material'
import { DMPConnectIndicator } from './DMPConnectIndicator'
import { useOfflineMode } from '@/providers/OfflineModeProvider'
import { isPDS } from '@/utils/type-guards'
import { HeaderIconButton } from './HeaderIconButton'
import { PatientQueueButton } from './PatientQueueButton'
import { MAIN_HEADER_HEIGHT } from '../../model/constants'
import { TopbarOmnibox } from '@/features/dashboard-omnibox'

export type HeaderProps = {
  menuItems: SidebarItem[]
}

const NETWORK_SPEED_THRESHOLD = 2 // mbps

export const headerHeight = MAIN_HEADER_HEIGHT

export default function Header(props: HeaderProps) {
  const { menuItems } = props
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const currentPathPattern = usePathPattern()
  const { t } = useTranslation()
  const theme = useTheme()

  const appSettings = useAppSelector(selectAppSettings)

  const accountMenuRef = useRef<AccountMenuHandle>(null)

  const authUser = useAppSelector(selectAuthUser)
  const staffUser = isStaff(authUser) ? authUser : null
  const { data: timeTrackingConsent } = useGetTimeTrackingConsentQuery(undefined, { skip: !staffUser })
  const isTimeTrackingEnabled = !!timeTrackingConsent?.enabled
  const { isINSEnabled } = useIsINSEnabled()
  const isDmpConnectEnabled = useIsFlagEnabled(FeatureFlag.DmpConnect)
  const isOmniboxEnabled = useIsFlagEnabled(FeatureFlag.DashboardElaraOmnibox)
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })
  const networkSpeed = useAppSelector(selectNetworkSpeed)
  const subscription = useAppSelector(selectAuthUserSubscription)
  const [syncDialogOpen, setSyncDialogOpen] = useState(false)
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false)
  const { isOfflineMode } = useOfflineMode()

  // Service worker and cache refresh functionality

  const currentPageName = useMemo(() => {
    const matchedItem = _.find(menuItems, (menuItem) => currentPathPattern.startsWith(menuItem.link) && menuItem.link !== '/')
    return matchedItem ? t(matchedItem.label) : t('common:licenseComponent.sidebar.element.home')
  }, [menuItems, currentPathPattern, t])

  useEffect(() => {
    dispatch(getDownloadSpeed())
    dispatch(getUploadSpeed())
  }, [dispatch])

  // end event region
  const handleUpdateNetworkSpeed = () => {
    dispatch(getDownloadSpeed())
    dispatch(getUploadSpeed())
  }

  const handleLogout = () => {
    dispatch(logout())
  }

  const handleNavigateFromAccountMenu = (link: string) => () => {
    accountMenuRef.current?.closeMenu()
    navigate(link, { replace: true })
  }

  const handleOpenAboutDialog = () => {
    accountMenuRef.current?.closeMenu()
    setAboutDialogOpen(true)
  }

  // const onNotificationClose = useCallback(() => {
  //   events.forEach((event) => {
  //     if (!event.recipients.some((recipient) => recipient?.userId === authUser?._id && recipient.read)) {
  //       // mark as read
  //       dispatch(
  //         markNotificationAsRead({
  //           eventId: event._id,
  //         })
  //       )
  //     }
  //   })
  // }, [dispatch, events, authUser])

  return (
    <AppBar position="sticky" role="banner" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Container maxWidth={false}>
        <Toolbar disableGutters sx={{ minHeight: 'unset !important', height: `${headerHeight}px` }}>
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
            <Button component={RouterLink} to="/">
              <img alt="MedConnect Professional" src={authUser?.establishment?.logo?.url ?? logoImg} height="36px" />
            </Button>
          </Box>
          {isMobileScreen && (
            <Stack>
              <Typography>{currentPageName}</Typography>
            </Stack>
          )}
          <Stack direction="row" sx={{ flexGrow: 1 }}>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{ display: { xs: 'none', md: 'flex' }, color: (theme) => theme.palette.common.white }}
            >
              {_.map(
                _.filter(menuItems, (menuItem) => menuItem.isPathBase || currentPathPattern.startsWith(menuItem.link)),
                (menuItem) => {
                  return (
                    <Link
                      key={menuItem.name}
                      component={RouterLink}
                      underline="none"
                      sx={{ display: 'flex', alignItems: 'center' }}
                      color="inherit"
                      to={menuItem.link}
                    >
                      {t(menuItem.label)}
                    </Link>
                  )
                }
              )}
            </Breadcrumbs>
            <Stack direction="row" flex={1} alignItems="center" justifyContent="end" spacing={1}>
              {isOmniboxEnabled && <TopbarOmnibox />}
              <Stack display={isMobileScreen || isMediumScreen ? 'none' : 'flex'} direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={1}>
                <IconButton onClick={handleUpdateNetworkSpeed} size="small" aria-label={t('header.networkSpeed.download.tooltip') as string}>
                  <CloudDownloadOutlinedIcon fontSize="small" />
                </IconButton>
                <Tooltip title={t('header.networkSpeed.download.tooltip')}>
                  <Typography
                    sx={[
                      { display: 'flex', alignItems: 'center', fontSize: '14px' },
                      !_.isNull(networkSpeed.download) &&
                        networkSpeed.download < NETWORK_SPEED_THRESHOLD && {
                          color: colors.red500,
                        },
                    ]}
                  >
                    {!_.isNull(networkSpeed.download) && networkSpeed.download < NETWORK_SPEED_THRESHOLD && <PriorityHighRoundedIcon fontSize="small" />}
                    {networkSpeed.download !== null ? networkSpeed.download : '-'} mbps
                  </Typography>
                </Tooltip>
              </Stack>
              <Stack display={isMobileScreen || isMediumScreen ? 'none' : 'flex'} direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={1}>
                <CloudUploadOutlinedIcon fontSize="small" />
                <Tooltip title={t('header.networkSpeed.upload.tooltip')}>
                  <Typography
                    sx={[
                      { display: 'flex', alignItems: 'center', fontSize: '14px' },
                      !_.isNull(networkSpeed.upload) &&
                        networkSpeed.upload < NETWORK_SPEED_THRESHOLD && {
                          color: colors.red500,
                        },
                    ]}
                  >
                    {!_.isNull(networkSpeed.upload) && networkSpeed.upload < NETWORK_SPEED_THRESHOLD && <PriorityHighRoundedIcon fontSize="small" />}
                    {networkSpeed.upload !== null ? networkSpeed.upload : '-'} mbps
                  </Typography>
                </Tooltip>
              </Stack>
              {appSettings.showPatientQueue && isPDS(authUser) && <PatientQueueButton />}
              <Notifications />
              {isTimeTrackingEnabled && <TimeTrackingNotifications />}
              {isDmpConnectEnabled && isINSEnabled && <DMPConnectIndicator />}
              {appSettings.showOfflineButton && (
                <>
                  <OfflineModeBadge />
                  <Tooltip title={isOfflineMode ? 'You are in offline mode' : 'Offline mode & sync options'}>
                    <HeaderIconButton
                      color="primary"
                      aria-label={isOfflineMode ? 'You are in offline mode' : 'Offline mode & sync options'}
                      onClick={() => setSyncDialogOpen(true)}
                      sx={{
                        display: 'flex',
                        position: 'relative',
                        backgroundColor: isOfflineMode ? 'warning.main' : 'transparent',
                        '&:hover': {
                          backgroundColor: isOfflineMode ? 'warning.dark' : theme.palette.primary.light,
                        },
                      }}
                    >
                      <SyncIcon />
                    </HeaderIconButton>
                  </Tooltip>
                </>
              )}
              <Stack display={isMobileScreen || isMediumScreen ? 'none' : 'flex'} direction="row" alignItems="center" spacing={1}>
                <Language />
                <Typography
                  noWrap
                  variant="h6"
                  component="div"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: (theme) => theme.palette.common.white,
                    fontSize: '16px',
                  }}
                >
                  {authUser?.userInfo.surname} {authUser?.userInfo.name}
                </Typography>
              </Stack>
              {!isMobileScreen && (
                <AccountMenu ref={accountMenuRef}>
                  <MenuItem onClick={handleNavigateFromAccountMenu('/configuration')}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    {t('header.accountMenu.configuration')}
                  </MenuItem>
                  {isStaff(authUser) && (
                    <MenuItem onClick={handleNavigateFromAccountMenu('/licenses')}>
                      <ListItemIcon>
                        <BadgeIcon />
                      </ListItemIcon>
                      {t('header.accountMenu.licenses')}
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleOpenAboutDialog} data-testid="account-menu-about">
                    <ListItemIcon>
                      <InfoOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    {t('header.accountMenu.about')}
                  </MenuItem>
                  <MenuItem onClick={handleLogout} data-testid="account-menu-logout">
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    {t('header.accountMenu.logout')}
                  </MenuItem>
                </AccountMenu>
              )}
              {isMobileScreen && <SidebarMobile menuItems={menuItems} />}
            </Stack>
          </Stack>
          <SyncDialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} />
          <AboutDialog open={aboutDialogOpen} onClose={() => setAboutDialogOpen(false)} />
        </Toolbar>
      </Container>
    </AppBar>
  )
}
